import React, { useRef } from 'react';
import { useNavigateAndScroll } from '../../Hooks/useNavigateAndScroll';

const FAQs = () => {
  const barRef = useRef(null);
  const navigateAndScroll = useNavigateAndScroll();
  const infoCardsData = {
    newPatient: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/New+Pt+registration.webp',
      infoTitle: 'New Patient Registration',
      infoText: 'Quickly register online for your first visit. Fill out personal, insurance, and health history details to get started with our practice in just a few minutes.',
      path: '/patient-center/new-patient'
    },
    patientForms: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Patient+forms.webp',
      infoTitle: 'Patient Forms',
      infoText: 'Access all necessary intake and consent forms to streamline your visit. Download, complete, and upload them securely before your appointment.',
      path: '/patient-center/patient-forms'
    },
    refillRequests: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Med+refill.webp',
      infoTitle: 'Refill Requests',
      infoText: 'Submit medication refill requests online anytime. Our team will process them promptly and notify you when they’re ready for pickup or sent to your pharmacy.',
      path: '/patient-center/refill-requests'
    },
    officePolicies: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Office+Policy.webp',
      infoTitle: 'Office Policies',
      infoText: 'Review our appointment, cancellation, and billing policies to ensure a smooth experience. Everything you need to know about visits, payments, and patient responsibilities.',
      path: '/patient-center/office-policies'
    },
    faqs: {
      image: 'https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/FAQ.webp',
      infoTitle: "FAQ's",
      infoText: 'Find quick answers to the most common questions—hours of operation, new patient procedures, after-hours care, and more—all in one place.',
      path: '/patient-center/faqs'
    }
  };
  return (
    <div>
    <div className="relative w-auto z-1 mt-[-70px]">
          <img
            className="w-full h-auto rounded-lg shadow-lg mt-2"
            src="https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/FAQ+submenu_InPixio.webp"
            alt="Mother and child meeting a doctor"
          />
        </div>
    <div ref={barRef} className="sticky top-0 flex justify-around bg-custom-blue text-white z-50">
        {Object.values(infoCardsData).map((card) => (
          <button
            key={card.infoTitle}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => navigateAndScroll(card.path, card.infoTitle)}
          >
            {card.infoTitle}
          </button>
        ))}
      </div>
    <div className="w-full px-4 py-8 sm:px-6 lg:px-64">
      {/* Title */}
      <h2 className="text-4xl font-bold text-gray-800 mb-2">FAQs</h2>
      <hr className="border-t-2 border-blue-500 mb-6" />

      <div className="space-y-6 text-gray-600">
        {/* Intro */}
        <section>
          <p className="mb-4">
            Please take a moment to read our most commonly asked questions. We're always available to answer your questions and encourage you to contact our office if you have a question that is not answered below.
          </p>
        </section>

        {/* Q1 */}
        <section>
          <p className="font-bold text-gray-800 mb-1">
            Q: What is a Pediatrician?
          </p>
          <p className="mb-4">
            A: A pediatrician is a medical doctor who specializes in the care of children. Pediatricians have undergone special training in the health and illnesses of infants, teens, and young adults, and the majority of pediatricians are certified by the American Board of Pediatrics after passing a comprehensive exam.
          </p>
          <p className="mb-4">
            Pediatricians provide preventive health care for children in good health and medical care for children who are acutely or chronically ill. They also provide parents with support and advice on issues such as growth and development, safety and prevention, nutrition, and emotional wellness to foster a lifetime of good health.
          </p>
        </section>

        {/* Q2 */}
        <section>
          <p className="font-bold text-gray-800 mb-1">
            Q: Can I meet my pediatrician before my baby is born?
          </p>
          <p className="mb-4">
            A: Yes, in fact we strongly encourage parents-to-be to visit our office for a prenatal appointment. This is a great way to get acquainted with our office and our doctors. During this visit, we will answer any questions that you have about our practice or your new child. Visit our expectant parent's page for more information.
          </p>
        </section>

        {/* Q3 */}
        <section>
          <p className="font-bold text-gray-800 mb-1">
            Q: How often should my child see the pediatrician?
          </p>
          <p className="mb-4">
            A: Your child should not only see the pediatrician for an illness. It is also important to schedule well-child-care exams regularly, beginning in infancy. Also called well-care visits or checkups, these routine examinations provide the best opportunity for the doctor to observe the progress of your child's physical and mental growth and development; to counsel and teach parents; to detect problems through screening tests; to provide immunizations; and to get to know one another. Well-care visits are strongly recommended as part of preventive pediatric care.
          </p>
          <p className="mb-4">
            Well-child visits are also a good time for parents to raise questions and concerns about a child's development, behavior, nutrition, safety, and overall well-being.
          </p>
          <p className="mb-4">
            The American Academy of Pediatrics recommends this schedule for routine well-care visits:
          </p>
          <ul className="list-disc list-outside ml-6 mb-4 space-y-1">
            <li>3 to 5 days</li>
            <li>1 month</li>
            <li>2 months</li>
            <li>4 months</li>
            <li>6 months</li>
            <li>9 months</li>
            <li>12 months</li>
            <li>15 months</li>
            <li>18 months</li>
            <li>24 months</li>
            <li>30 months</li>
            <li>3 years</li>
            <li>4 years</li>
            <li>And once every year thereafter for an annual health supervision visit</li>
          </ul>
        </section>

        {/* Q4 */}
        <section>
          <p className="font-bold text-gray-800 mb-1">
            Q: What is the best way to schedule an appointment with your office?
          </p>
          <p className="mb-4">
            A: You can schedule an appointment by calling our office during regular business hours, or by requesting an appointment online.
          </p>
        </section>

        {/* Q5 */}
        <section>
          <p className="font-bold text-gray-800 mb-1">
            Q: Is your office accepting new patients?
          </p>
          <p className="mb-4">
            A: Yes, we always welcome new patients. Contact our office for additional information or request an appointment.
          </p>
        </section>

        {/* Q6 */}
        <section>
          <p className="font-bold text-gray-800 mb-1">
            Q: Why does my child need to receive vaccinations?
          </p>
          <p className="mb-4">
            A: Immunizations are a series of shots given to children at different ages to help ward off serious, and potentially fatal, childhood diseases. Making sure your child receives immunizations when scheduled is the best way to help protect your child from potentially fatal diseases. According to the American Academy of Pediatrics, vaccinations have reduced the number of infections from vaccine-preventable diseases by more than 90%. If you're apprehensive about vaccinations, please do not hesitate to contact our office.
          </p>
        </section>
      </div>
    </div>
    </div>
  );
};

export default FAQs;
